<template>
  <v-container fluid class="ncrm-products-page">
    <div class="ncrm-products__header" data-tour="products-header">
      <div class="ncrm-products__header-left">
        <v-icon class="ncrm-products__header-icon" size="16">mdi-package-variant-outline</v-icon>
        <h1 class="ncrm-products__title">{{ $t("ncrm.products.title") }}</h1>
        <span class="ncrm-products__subtitle">{{ $t("ncrm.products.subtitle") }}</span>
      </div>
      <div class="ncrm-products__header-right">
        <v-btn variant="text" color="primary" :loading="loading" @click="loadProducts">
          <v-icon start size="16">mdi-refresh</v-icon>
          {{ $t("ncrm.deals.actions.refresh") }}
        </v-btn>
        <v-btn color="primary" data-tour="products-create" @click="openCreateDialog">
          <v-icon start size="16">mdi-plus</v-icon>
          {{ $t("ncrm.products.actions.create") }}
        </v-btn>
        <PageHelpButton />
      </div>
    </div>
    <v-card class="mt-4">
      <v-card-text data-tour="products-filters">
        <v-row dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="filters.search"
              :label="$t('ncrm.products.filters.search')"
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              @keydown.enter.prevent="loadProducts"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.productType"
              :items="productTypeFilterOptions"
              item-title="label"
              item-value="value"
              :label="$t('ncrm.products.filters.type')"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.isActive"
              :items="activeFilterOptions"
              item-title="label"
              item-value="value"
              :label="$t('ncrm.products.filters.status')"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-end">
            <v-btn variant="text" color="primary" @click="resetFilters">
              {{ $t("ncrm.products.filters.clear") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <div class="d-flex align-center justify-space-between flex-wrap ga-2 px-4 pt-4">
        <NcrmPreferredViewTabs
          v-model="activeView"
          :items="viewItems"
          :preferred-value="preferredView"
          :aria-label="$t('ncrm.shared.viewPreferences.tabsAriaLabel')"
          @set-default="setPreferredView"
        />
      </div>
      <v-card-text v-if="activeView === 'board'" class="pt-4" data-tour="products-list">
        <div v-if="loading" class="ncrm-products-page__board-loading">
          <v-skeleton-loader type="article" />
          <v-skeleton-loader type="article" />
          <v-skeleton-loader type="article" />
        </div>

        <v-alert v-else-if="!products.length" type="info">
          {{ $t("ncrm.products.empty") }}
        </v-alert>

        <div v-else class="ncrm-products-page__cards-grid">
          <article
            v-for="item in products"
            :key="item.id"
            class="ncrm-products-page__board-card"
            role="button"
            tabindex="0"
            @click="openEditDialog(item)"
            @keydown.enter.prevent="openEditDialog(item)"
            @keydown.space.prevent="openEditDialog(item)"
          >
            <div class="d-flex align-start justify-space-between ga-3">
              <div class="d-flex align-start ga-3">
                <div class="ncrm-products-page__icon-badge">
                  <v-icon size="20" color="primary">{{ resolveProductIconName(item) }}</v-icon>
                </div>
                <div class="text-left">
                  <div class="text-body-1 ncrm-text-strong">
                    {{ item.name || `#${item.id}` }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ item.sku || productTypeLabel(item.product_type) }}
                  </div>
                </div>
              </div>

              <v-chip size="x-small" :color="productStatusColor(item)">
                {{ productStatusLabel(item) }}
              </v-chip>
            </div>

            <div class="d-flex flex-wrap ga-2 mt-4">
              <v-chip color="primary">
                {{ productTypeLabel(item.product_type) }}
              </v-chip>
              <v-chip color="info">
                {{ billingPeriodLabel(item.billing_period, item.product_type) }}
              </v-chip>
            </div>

            <div class="ncrm-products-page__price mt-4">
              {{ formatMoney(item.default_unit_price, item.default_currency) }}
            </div>

            <div class="text-caption text-medium-emphasis mt-4">
              {{ $t("ncrm.products.headers.updatedAt") }}: {{ formatDateTime(item.updated_at) }}
            </div>
          </article>
        </div>
      </v-card-text>
      <v-card-text v-else class="pa-0" data-tour="products-list">
        <v-data-table
          :headers="headers"
          :items="products"
          item-key="id"
          density="comfortable"
          class="elevation-0 ncrm-products__table"
          :loading="loading"
        >
          <template #item.name="{ item }">
            <div class="d-flex align-center ga-3">
              <div class="ncrm-products-page__icon-badge ncrm-products-page__icon-badge--compact">
                <v-icon size="18" color="primary">{{ resolveProductIconName(item) }}</v-icon>
              </div>
              <div>
                <div class="text-body-2 ncrm-text-strong">{{ item.name || `#${item.id}` }}</div>
                <div v-if="item.sku" class="text-caption text-medium-emphasis">{{ item.sku }}</div>
              </div>
            </div>
          </template>

          <template #item.product_type="{ item }">
            <v-chip color="primary">
              {{ productTypeLabel(item.product_type) }}
            </v-chip>
          </template>

          <template #item.default_unit_price="{ item }">
            {{ formatMoney(item.default_unit_price, item.default_currency) }}
          </template>

          <template #item.billing_period="{ item }">
            {{ billingPeriodLabel(item.billing_period, item.product_type) }}
          </template>

          <template #item.is_active="{ item }">
            <v-chip :color="productStatusColor(item)">
              {{ productStatusLabel(item) }}
            </v-chip>
          </template>

          <template #item.updated_at="{ item }">
            {{ formatDateTime(item.updated_at) }}
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex align-center justify-end ga-1">
              <v-btn size="small" variant="text" color="primary" @click="openEditDialog(item)">
                <v-icon start>mdi-pencil</v-icon>
                {{ $t("app.actions.edit") }}
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                :color="isProductActive(item) ? 'warning' : 'success'"
                @click="toggleProductStatus(item)"
              >
                <v-icon start>{{ isProductActive(item) ? "mdi-pause-circle-outline" : "mdi-play-circle-outline" }}</v-icon>
                {{ isProductActive(item) ? $t("ncrm.products.actions.deactivate") : $t("ncrm.products.actions.activate") }}
              </v-btn>
            </div>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.products.empty") }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editDialog" max-width="760">
      <v-card>
        <v-card-title>
          {{ editingId ? $t("ncrm.products.dialog.editTitle") : $t("ncrm.products.dialog.createTitle") }}
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                :label="$t('ncrm.products.fields.name')"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.product_type"
                :items="productTypeOptions"
                item-title="label"
                item-value="value"
                :label="$t('ncrm.products.fields.productType')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.sku"
                :label="$t('ncrm.products.fields.sku')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <NcrmIconGridPicker
                v-model="form.icon_key"
                :items="productIconOptions"
                :label="$t('ncrm.products.fields.icon')"
                :dialog-title="$t('ncrm.products.iconPicker.title')"
                :search-label="$t('ncrm.products.iconPicker.search')"
                :empty-text="$t('ncrm.products.iconPicker.empty')"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.default_unit_price"
                :label="$t('ncrm.products.fields.defaultUnitPrice')"
                type="number"
                min="0"
                step="0.01"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="form.default_currency"
                :label="$t('ncrm.products.fields.defaultCurrency')"
                maxlength="8"
              />
            </v-col>
            <v-col cols="12" md="6" v-if="form.product_type === 'subscription'">
              <v-select
                v-model="form.billing_period"
                :items="billingPeriodOptions"
                item-title="label"
                item-value="value"
                :label="$t('ncrm.products.fields.billingPeriod')"
              />
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-switch
                v-model="form.is_active"
                inset
                color="primary"
                :label="$t('ncrm.products.fields.isActive')"
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="editDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="saving || !canSubmit" @click="submitForm">
            {{ editingId ? $t("app.actions.save") : $t("app.actions.create") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmIconGridPicker from "@/components/ncrm/shared/NcrmIconGridPicker.vue";
import NcrmPreferredViewTabs from "@/components/ncrm/shared/NcrmPreferredViewTabs.vue";
import PageHelpButton from "@/components/tours/PageHelpButton.vue";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import { formatDisplayAmount, formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

const PRODUCT_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
const PRODUCTS_PAGE_KEY_FALLBACK = "/ncrm/products";
const PRODUCT_ICON_OPTION_DEFS = Object.freeze([
  { value: "", icon: "mdi-auto-fix", labelKey: "ncrm.products.icons.auto" },
  { value: "menu", icon: "mdi-silverware-fork-knife", labelKey: "ncrm.products.icons.menu" },
  { value: "website", icon: "mdi-web", labelKey: "ncrm.products.icons.website" },
  { value: "qr", icon: "mdi-qrcode", labelKey: "ncrm.products.icons.qr" },
  { value: "subscription", icon: "mdi-repeat", labelKey: "ncrm.products.icons.subscription" },
  { value: "setup", icon: "mdi-cog-outline", labelKey: "ncrm.products.icons.setup" },
  { value: "delivery", icon: "mdi-truck-delivery-outline", labelKey: "ncrm.products.icons.delivery" },
  { value: "support", icon: "mdi-headset", labelKey: "ncrm.products.icons.support" },
  { value: "marketing", icon: "mdi-bullhorn-outline", labelKey: "ncrm.products.icons.marketing" },
  { value: "analytics", icon: "mdi-chart-line", labelKey: "ncrm.products.icons.analytics" },
  { value: "payments", icon: "mdi-credit-card-outline", labelKey: "ncrm.products.icons.payments" },
  { value: "package", icon: "mdi-package-variant-closed", labelKey: "ncrm.products.icons.package" },
  { value: "store", icon: "mdi-storefront-outline", labelKey: "ncrm.products.icons.store" },
  { value: "restaurant", icon: "mdi-food-outline", labelKey: "ncrm.products.icons.restaurant" },
  { value: "hotel", icon: "mdi-bed-outline", labelKey: "ncrm.products.icons.hotel" },
  { value: "beauty", icon: "mdi-mirror", labelKey: "ncrm.products.icons.beauty" },
  { value: "fitness", icon: "mdi-dumbbell", labelKey: "ncrm.products.icons.fitness" },
  { value: "clinic", icon: "mdi-stethoscope", labelKey: "ncrm.products.icons.clinic" },
  { value: "education", icon: "mdi-school-outline", labelKey: "ncrm.products.icons.education" },
  { value: "events", icon: "mdi-calendar-star", labelKey: "ncrm.products.icons.events" },
  { value: "ecommerce", icon: "mdi-cart-outline", labelKey: "ncrm.products.icons.ecommerce" },
  { value: "marketplace", icon: "mdi-shopping-outline", labelKey: "ncrm.products.icons.marketplace" },
  { value: "mobile", icon: "mdi-cellphone", labelKey: "ncrm.products.icons.mobile" },
  { value: "app", icon: "mdi-apps", labelKey: "ncrm.products.icons.app" },
  { value: "desktop", icon: "mdi-monitor", labelKey: "ncrm.products.icons.desktop" },
  { value: "cloud", icon: "mdi-cloud-outline", labelKey: "ncrm.products.icons.cloud" },
  { value: "server", icon: "mdi-server", labelKey: "ncrm.products.icons.server" },
  { value: "security", icon: "mdi-shield-check-outline", labelKey: "ncrm.products.icons.security" },
  { value: "wifi", icon: "mdi-wifi", labelKey: "ncrm.products.icons.wifi" },
  { value: "booking", icon: "mdi-calendar-check-outline", labelKey: "ncrm.products.icons.booking" },
  { value: "chat", icon: "mdi-chat-processing-outline", labelKey: "ncrm.products.icons.chat" },
  { value: "email", icon: "mdi-email-outline", labelKey: "ncrm.products.icons.email" },
  { value: "phone", icon: "mdi-phone-outline", labelKey: "ncrm.products.icons.phone" },
  { value: "sms", icon: "mdi-message-text-outline", labelKey: "ncrm.products.icons.sms" },
  { value: "ads", icon: "mdi-bullseye-arrow", labelKey: "ncrm.products.icons.ads" },
  { value: "social", icon: "mdi-account-group-outline", labelKey: "ncrm.products.icons.social" },
  { value: "seo", icon: "mdi-magnify", labelKey: "ncrm.products.icons.seo" },
  { value: "crm", icon: "mdi-account-multiple-outline", labelKey: "ncrm.products.icons.crm" },
  { value: "automation", icon: "mdi-robot-outline", labelKey: "ncrm.products.icons.automation" },
  { value: "reports", icon: "mdi-file-chart-outline", labelKey: "ncrm.products.icons.reports" },
  { value: "invoices", icon: "mdi-file-document-outline", labelKey: "ncrm.products.icons.invoices" },
  { value: "pos", icon: "mdi-cash-register", labelKey: "ncrm.products.icons.pos" },
  { value: "printer", icon: "mdi-printer-outline", labelKey: "ncrm.products.icons.printer" },
  { value: "kiosk", icon: "mdi-tablet-cellphone", labelKey: "ncrm.products.icons.kiosk" },
  { value: "camera", icon: "mdi-camera-outline", labelKey: "ncrm.products.icons.camera" },
  { value: "map", icon: "mdi-map-marker-outline", labelKey: "ncrm.products.icons.map" },
  { value: "loyalty", icon: "mdi-star-circle-outline", labelKey: "ncrm.products.icons.loyalty" },
  { value: "gift", icon: "mdi-gift-outline", labelKey: "ncrm.products.icons.gift" },
  { value: "calendar", icon: "mdi-calendar-month-outline", labelKey: "ncrm.products.icons.calendar" },
  { value: "team", icon: "mdi-account-group-outline", labelKey: "ncrm.products.icons.team" },
  { value: "training", icon: "mdi-presentation", labelKey: "ncrm.products.icons.training" },
  { value: "consulting", icon: "mdi-briefcase-outline", labelKey: "ncrm.products.icons.consulting" },
  { value: "maintenance", icon: "mdi-tools", labelKey: "ncrm.products.icons.maintenance" },
  { value: "other", icon: "mdi-shape-outline", labelKey: "ncrm.products.icons.other" },
]);
const PRODUCT_ICON_NAME_BY_KEY = Object.freeze(
  PRODUCT_ICON_OPTION_DEFS.reduce((accumulator, item) => {
    if (item.value) {
      accumulator[item.value] = item.icon;
    }
    return accumulator;
  }, {})
);
const PRODUCT_DEFAULT_ICON_KEY_BY_TYPE = Object.freeze({
  physical: "package",
  digital_one_time: "setup",
  subscription: "subscription",
});

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default {
  name: "NcrmProducts",
  components: {
    NcrmIconGridPicker,
    NcrmPreferredViewTabs,
    PageHelpButton,
  },
  data() {
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, PRODUCTS_PAGE_KEY_FALLBACK),
        items: PRODUCT_VIEW_ITEMS,
        fallback: "board",
      }) || "board";

    return {
      loading: false,
      saving: false,
      activeView: preferredView,
      preferredView,
      editDialog: false,
      editingId: null,
      products: [],
      filters: {
        search: "",
        productType: "all",
        isActive: "active",
      },
      form: {
        name: "",
        product_type: "digital_one_time",
        sku: "",
        icon_key: "",
        default_unit_price: "0",
        default_currency: "MDL",
        billing_period: "",
        is_active: true,
      },
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    viewItems() {
      return [
        { value: "board", label: this.$t("ncrm.products.views.board") },
        { value: "table", label: this.$t("ncrm.products.views.table") },
      ];
    },
    headers() {
      return [
        { title: this.$t("ncrm.products.headers.name"), key: "name" },
        { title: this.$t("ncrm.products.headers.type"), key: "product_type" },
        { title: this.$t("ncrm.products.headers.price"), key: "default_unit_price" },
        { title: this.$t("ncrm.products.headers.billing"), key: "billing_period" },
        { title: this.$t("ncrm.products.headers.status"), key: "is_active" },
        { title: this.$t("ncrm.products.headers.updatedAt"), key: "updated_at" },
        { title: this.$t("ncrm.products.headers.actions"), key: "actions", align: "end", sortable: false },
      ];
    },
    productTypeOptions() {
      return [
        { value: "physical", label: this.productTypeLabel("physical") },
        { value: "digital_one_time", label: this.productTypeLabel("digital_one_time") },
        { value: "subscription", label: this.productTypeLabel("subscription") },
      ];
    },
    productTypeFilterOptions() {
      return [
        { value: "all", label: this.$t("ncrm.products.filters.allTypes") },
        ...this.productTypeOptions,
      ];
    },
    productIconOptions() {
      return PRODUCT_ICON_OPTION_DEFS.map((item) => ({
        value: item.value,
        icon: item.icon,
        label: this.$t(item.labelKey),
      }));
    },
    billingPeriodOptions() {
      return [
        { value: "monthly", label: this.billingPeriodLabel("monthly") },
        { value: "yearly", label: this.billingPeriodLabel("yearly") },
      ];
    },
    activeFilterOptions() {
      return [
        { value: "all", label: this.$t("ncrm.products.filters.allStatuses") },
        { value: "active", label: this.$t("ncrm.products.status.active") },
        { value: "inactive", label: this.$t("ncrm.products.status.inactive") },
      ];
    },
    canSubmit() {
      const name = String(this.form.name || "").trim();
      if (!name) return false;
      if (this.form.product_type === "subscription") {
        return Boolean(String(this.form.billing_period || "").trim());
      }
      return true;
    },
  },
  watch: {
    "filters.productType"() {
      this.loadProducts();
    },
    "filters.isActive"() {
      this.loadProducts();
    },
    "form.product_type"(value) {
      if (value !== "subscription") {
        this.form.billing_period = "";
      }
    },
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, PRODUCTS_PAGE_KEY_FALLBACK);
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: PRODUCT_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      return formatDisplayDateTime(parsed);
    },
    formatMoney(value, currency) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return "-";
      const code = String(currency || "MDL").trim().toUpperCase() || "MDL";
      return `${formatDisplayAmount(parsed)} ${code}`;
    },
    isProductActive(item) {
      return Number(item?.is_active) === 1 || item?.is_active === true;
    },
    productStatusLabel(item) {
      return this.isProductActive(item)
        ? this.$t("ncrm.products.status.active")
        : this.$t("ncrm.products.status.inactive");
    },
    productStatusColor(item) {
      return this.isProductActive(item) ? "success" : "neutral";
    },
    productTypeLabel(type) {
      const normalized = String(type || "").trim().toLowerCase();
      return this.$t(`ncrm.products.types.${normalized}`) || normalized || "-";
    },
    billingPeriodLabel(period, productType = "") {
      const normalized = String(period || "").trim().toLowerCase();
      if (!normalized) {
        const normalizedProductType = String(productType || "").trim().toLowerCase();
        if (normalizedProductType && normalizedProductType !== "subscription") {
          return this.$t("ncrm.products.billing.one_time");
        }
        return "-";
      }
      return this.$t(`ncrm.products.billing.${normalized}`) || normalized;
    },
    resolveProductIconKey(item) {
      const explicitKey = String(item?.icon_key ?? item?.iconKey ?? "").trim().toLowerCase();
      if (explicitKey && PRODUCT_ICON_NAME_BY_KEY[explicitKey]) {
        return explicitKey;
      }
      const productType = String(item?.product_type || "").trim().toLowerCase();
      return PRODUCT_DEFAULT_ICON_KEY_BY_TYPE[productType] || "other";
    },
    resolveProductIconName(item) {
      const iconKey = this.resolveProductIconKey(item);
      return PRODUCT_ICON_NAME_BY_KEY[iconKey] || PRODUCT_ICON_NAME_BY_KEY.other;
    },
    buildListParams() {
      const params = {
        limit: 200,
        page: 1,
      };
      const search = String(this.filters.search || "").trim();
      if (search) params.search = search;
      if (this.filters.productType !== "all") params.product_type = this.filters.productType;
      if (this.filters.isActive === "active") params.is_active = true;
      if (this.filters.isActive === "inactive") params.is_active = false;
      return params;
    },
    async loadProducts() {
      this.loading = true;
      try {
        const { data } = await axios.get("/ncrm/products", {
          params: this.buildListParams(),
        });
        this.products = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm-products] load error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.products.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        search: "",
        productType: "all",
        isActive: "active",
      };
      this.loadProducts();
    },
    resetForm() {
      this.form = {
        name: "",
        product_type: "digital_one_time",
        sku: "",
        icon_key: "",
        default_unit_price: "0",
        default_currency: "MDL",
        billing_period: "",
        is_active: true,
      };
    },
    openCreateDialog() {
      this.editingId = null;
      this.resetForm();
      this.editDialog = true;
    },
    openEditDialog(item) {
      this.editingId = Number(item?.id || 0) || null;
      this.form = {
        name: item?.name || "",
        product_type: item?.product_type || "digital_one_time",
        sku: item?.sku || "",
        icon_key: String(item?.icon_key || ""),
        default_unit_price: String(toNumber(item?.default_unit_price, 0)),
        default_currency: String(item?.default_currency || "MDL").toUpperCase(),
        billing_period: item?.billing_period || "",
        is_active: Number(item?.is_active) === 1,
      };
      this.editDialog = true;
    },
    buildPayload() {
      return {
        name: String(this.form.name || "").trim(),
        product_type: this.form.product_type,
        sku: String(this.form.sku || "").trim() || null,
        icon_key: String(this.form.icon_key || "").trim().toLowerCase() || null,
        default_unit_price: this.form.default_unit_price === "" ? 0 : Number(this.form.default_unit_price),
        default_currency: String(this.form.default_currency || "MDL").trim().toUpperCase(),
        billing_period:
          this.form.product_type === "subscription"
            ? String(this.form.billing_period || "").trim() || null
            : null,
        is_active: this.form.is_active === true,
      };
    },
    async submitForm() {
      if (!this.canSubmit) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.editingId) {
          await axios.patch(`/ncrm/products/${this.editingId}`, payload);
          this.showSnackbar(this.$t("ncrm.products.messages.updated"), "success");
        } else {
          await axios.post("/ncrm/products", payload);
          this.showSnackbar(this.$t("ncrm.products.messages.created"), "success");
        }
        this.editDialog = false;
        await this.loadProducts();
      } catch (error) {
        console.error("[ncrm-products] save error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.products.messages.saveError"),
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async toggleProductStatus(item) {
      const productId = Number(item?.id || 0);
      if (!productId) return;
      try {
        await axios.patch(`/ncrm/products/${productId}`, {
          is_active: !this.isProductActive(item),
        });
        this.showSnackbar(this.$t("ncrm.products.messages.updated"), "success");
        await this.loadProducts();
      } catch (error) {
        console.error("[ncrm-products] toggle status error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.products.messages.saveError"),
          "error"
        );
      }
    },
  },
};
</script>

<style scoped>
.ncrm-products-page {
  padding-bottom: 24px;
}

/* ── Breadcrumb strip ─────────────────────────────────────── */
.ncrm-products__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 2px 0 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

.ncrm-products__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1 1 0;
}

.ncrm-products__header-icon {
  flex-shrink: 0;
  color: rgb(var(--v-theme-tertiary));
  opacity: 0.85;
}

.ncrm-products__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  color: var(--app-text-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-products__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.6;
  white-space: nowrap;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  margin-left: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-products__header-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 960px) {
  .ncrm-products__header { align-items: flex-start; }
  .ncrm-products__header-right { width: 100%; justify-content: flex-end; }
  .ncrm-products__subtitle { display: none; }
}

.ncrm-products-page__board-loading {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.ncrm-products-page__cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.ncrm-products-page__board-card {
  min-height: 210px;
  padding: 18px;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ncrm-products-page__board-card:hover,
.ncrm-products-page__board-card:focus-visible {
  border-color: var(--ncrm-color-primary-border);
  box-shadow: 0 8px 20px rgba(var(--v-theme-on-surface), 0.08);
  transform: translateY(-1px);
  outline: none;
}

.ncrm-products-page__icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  background: var(--ncrm-color-surface-muted);
  flex: 0 0 auto;
}

.ncrm-products-page__icon-badge--compact {
  width: 36px;
  height: 36px;
}

.ncrm-products-page__price {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
}
</style>
